/**
 * FERP Portal - Annotation Panel (DRR-062)
 *
 * Data-driven contextual annotation panel that embeds in any entity detail view.
 * Shows annotations (comments, notes, escalations, decisions) for a given entity
 * with threaded reply support.
 *
 * FERP CONSTRAINTS:
 * - Mantine v8.x ONLY (NO MUI)
 * - Backend ID generation (Snowflake IDs via createAnnotation mutation)
 * - Repository pattern via generated Apollo hooks
 * - Multi-tenant isolation via AuthCtx (tenant from auth, not props)
 * - Append-only: archive with reason, no hard delete
 * - Data-driven: type/visibility configs from centralized config
 */

import { useState, useCallback } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Button,
  Select,
  Paper,
  ActionIcon,
  Tooltip,
  Collapse,
  Loader,
  Center,
  Alert,
  Divider,
  ThemeIcon,
  Box,
} from '@mantine/core';
import { MentionInput, type MentionSuggestion } from './MentionInput';
import { useDisclosure } from '@mantine/hooks';
import {
  IconMessage,
  IconSend,
  IconChevronDown,
  IconChevronRight,
  IconArchive,
  IconAlertTriangle,
} from '@tabler/icons-react';
import {
  ANNOTATION_TYPE,
  ANNOTATION_VISIBILITY,
  getAnnotationTypeConfig,
  getAnnotationTypeOptions,
  getAnnotationVisibilityConfig,
} from '@/config/annotations';
import type { AnnotationEntity } from '@/normalizers/annotations';

// ============================================================================
// TYPES
// ============================================================================

interface AnnotationPanelProps {
  /** Target entity type (e.g., 'task', 'approval', 'workflow') */
  entityType: string;
  /** Target entity ID */
  entityId: string;
  /** Annotations data — passed in from parent (data-driven) */
  annotations: AnnotationEntity[];
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** Callback when user submits a new annotation */
  onCreateAnnotation?: (input: CreateAnnotationInput) => void;
  /** Callback when user archives an annotation */
  onArchiveAnnotation?: (annotationId: string, reason: string) => void;
  /** Callback to load a thread */
  onLoadThread?: (threadRootId: string) => void;
  /** Thread data keyed by threadRootId */
  threads?: Record<string, AnnotationEntity[]>;
  /** Whether the current user has write permission */
  canWrite?: boolean;
  /** Whether the current user has admin permission (archive) */
  canAdmin?: boolean;
  /** User's auth scopes — filters which annotation types are available */
  userScopes?: string[];
  /** Search callback for @mention autocomplete */
  onSearchMentions?: (query: string) => Promise<MentionSuggestion[]>;
}

export interface CreateAnnotationInput {
  entityType: string;
  entityId: string;
  parentId?: string;
  body: string;
  mentions?: string[];
  visibility: string;
  annotationType: string;
  workflowInstanceId?: string;
  relatedEntityIds?: string[];
}

// ============================================================================
// ANNOTATION ITEM (data-driven rendering)
// ============================================================================

interface AnnotationItemProps {
  annotation: AnnotationEntity;
  onReply?: (parentId: string) => void;
  onArchive?: (annotationId: string, reason: string) => void;
  onLoadThread?: (threadRootId: string) => void;
  thread?: AnnotationEntity[];
  canAdmin?: boolean;
  depth?: number;
}

function AnnotationItem({
  annotation,
  onReply,
  onArchive,
  onLoadThread,
  thread,
  canAdmin,
  depth = 0,
}: AnnotationItemProps) {
  const [threadOpen, { toggle: toggleThread }] = useDisclosure(false);
  const typeConfig = getAnnotationTypeConfig(annotation.annotationType);
  const visConfig = getAnnotationVisibilityConfig(annotation.visibility);

  const hasReplies = annotation.replyCount > 0;
  const isReply = depth > 0;

  const formattedDate = new Date(annotation.createdAt).toLocaleString();

  return (
    <Paper
      p="sm"
      withBorder={!isReply}
      ml={isReply ? depth * 24 : 0}
      style={
        annotation.archived
          ? { opacity: 0.6, borderLeft: '3px solid var(--mantine-color-gray-4)' }
          : undefined
      }
    >
      <Stack gap="xs">
        {/* Header: author + type badge + visibility + timestamp */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <ThemeIcon size="sm" color={typeConfig.color} variant="light">
              <typeConfig.icon size={14} />
            </ThemeIcon>
            <Text fw={600} size="sm">
              {annotation.createdBy}
            </Text>
            <Badge size="xs" color={typeConfig.color} variant="light">
              {typeConfig.label}
            </Badge>
            {annotation.visibility !== 'INTERNAL' && (
              <Badge size="xs" color={visConfig.color} variant="outline">
                {visConfig.label}
              </Badge>
            )}
          </Group>
          <Group gap="xs">
            <Text size="xs" c="dimmed">
              {formattedDate}
            </Text>
            {canAdmin && !annotation.archived && onArchive && (
              <Tooltip label="Archive annotation">
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="gray"
                  onClick={() => {
                    const reason = window.prompt(
                      'Archive reason (required for audit trail):',
                    );
                    if (reason) {
                      onArchive(annotation.id, reason);
                    }
                  }}
                >
                  <IconArchive size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Group>

        {/* Archived indicator */}
        {annotation.archived && (
          <Alert
            color="gray"
            variant="light"
            p="xs"
            icon={<IconArchive size={14} />}
          >
            <Text size="xs">
              Archived: {annotation.archivedReason ?? 'No reason provided'}
            </Text>
          </Alert>
        )}

        {/* Body */}
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {annotation.body}
        </Text>

        {/* Mentions */}
        {annotation.mentions.length > 0 && (
          <Group gap={4}>
            {annotation.mentions.map((mention) => (
              <Badge key={mention} size="xs" variant="dot" color="blue">
                @{mention}
              </Badge>
            ))}
          </Group>
        )}

        {/* Reply / Thread actions */}
        <Group gap="xs">
          {onReply && !annotation.archived && (
            <Button
              size="compact-xs"
              variant="subtle"
              leftSection={<IconMessage size={12} />}
              onClick={() => onReply(annotation.id)}
            >
              Reply
            </Button>
          )}
          {hasReplies && (
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              leftSection={
                threadOpen ? (
                  <IconChevronDown size={12} />
                ) : (
                  <IconChevronRight size={12} />
                )
              }
              onClick={() => {
                if (!threadOpen && onLoadThread) {
                  onLoadThread(annotation.threadRootId ?? annotation.id);
                }
                toggleThread();
              }}
            >
              {annotation.replyCount}{' '}
              {annotation.replyCount === 1 ? 'reply' : 'replies'}
            </Button>
          )}
        </Group>

        {/* Thread replies */}
        <Collapse in={threadOpen}>
          {thread ? (
            <Stack gap="xs" mt="xs">
              {thread
                .filter((r) => r.id !== annotation.id)
                .map((reply) => (
                  <AnnotationItem
                    key={reply.id}
                    annotation={reply}
                    onReply={onReply}
                    onArchive={onArchive}
                    canAdmin={canAdmin}
                    depth={depth + 1}
                  />
                ))}
            </Stack>
          ) : (
            <Center py="xs">
              <Loader size="xs" />
            </Center>
          )}
        </Collapse>
      </Stack>
    </Paper>
  );
}

// ============================================================================
// COMPOSER (data-driven form)
// ============================================================================

interface AnnotationComposerProps {
  entityType: string;
  entityId: string;
  parentId?: string;
  onSubmit: (input: CreateAnnotationInput) => void;
  onCancel?: () => void;
  userScopes?: string[];
  onSearchMentions?: (query: string) => Promise<MentionSuggestion[]>;
}

function AnnotationComposer({
  entityType,
  entityId,
  parentId,
  onSubmit,
  onCancel,
  userScopes = [],
  onSearchMentions,
}: AnnotationComposerProps) {
  const [body, setBody] = useState('');
  const [annotationType, setAnnotationType] = useState<string>(
    ANNOTATION_TYPE.COMMENT,
  );
  const [visibility, setVisibility] = useState<string>(
    ANNOTATION_VISIBILITY.INTERNAL,
  );

  const typeConfig = getAnnotationTypeConfig(annotationType);

  const handleSubmit = useCallback(() => {
    if (!body.trim()) return;

    // Confirmation for escalations and decisions
    if (typeConfig.requiresConfirmation) {
      const confirmed = window.confirm(
        `Create ${typeConfig.label}? This will be recorded in the audit trail.`,
      );
      if (!confirmed) return;
    }

    onSubmit({
      entityType,
      entityId,
      parentId,
      body: body.trim(),
      visibility,
      annotationType,
    });

    setBody('');
  }, [
    body,
    entityType,
    entityId,
    parentId,
    visibility,
    annotationType,
    typeConfig,
    onSubmit,
  ]);

  // Build select options filtered by user scopes (data-driven)
  const typeOptions = getAnnotationTypeOptions(userScopes);

  const visibilityOptions = Object.entries(ANNOTATION_VISIBILITY).map(
    ([key, value]) => ({
      value,
      label: getAnnotationVisibilityConfig(value).label,
    }),
  );

  return (
    <Paper p="sm" withBorder>
      <Stack gap="xs">
        {parentId && (
          <Group gap="xs">
            <Text size="xs" c="dimmed">
              Replying to thread
            </Text>
            {onCancel && (
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                onClick={onCancel}
              >
                Cancel
              </Button>
            )}
          </Group>
        )}

        <MentionInput
          placeholder="Add a comment..."
          value={body}
          onChange={setBody}
          onSearchMentions={onSearchMentions}
          minRows={2}
          maxRows={6}
        />

        <Group justify="space-between">
          <Group gap="xs">
            <Select
              size="xs"
              w={130}
              data={typeOptions}
              value={annotationType}
              onChange={(v) => v && setAnnotationType(v)}
              allowDeselect={false}
            />
            <Select
              size="xs"
              w={120}
              data={visibilityOptions}
              value={visibility}
              onChange={(v) => v && setVisibility(v)}
              allowDeselect={false}
            />
          </Group>
          <Button
            size="xs"
            leftSection={<IconSend size={14} />}
            onClick={handleSubmit}
            disabled={!body.trim()}
          >
            Send
          </Button>
        </Group>

        {annotationType === ANNOTATION_TYPE.ESCALATION && (
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertTriangle size={14} />}
            p="xs"
          >
            <Text size="xs">
              Escalations trigger immediate notifications to assigned actors.
            </Text>
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}

// ============================================================================
// ANNOTATION PANEL (main export)
// ============================================================================

/**
 * Data-driven Annotation Panel.
 *
 * Embeddable in any entity detail view. All data and callbacks are passed
 * as props — the panel does NOT fetch data itself (dumb frontend principle).
 *
 * @example
 * ```tsx
 * <AnnotationPanel
 *   entityType="task"
 *   entityId={task.id}
 *   annotations={annotationsData}
 *   loading={loading}
 *   onCreateAnnotation={handleCreate}
 *   onArchiveAnnotation={handleArchive}
 *   canWrite={hasScope('annotation:write')}
 *   canAdmin={hasScope('annotation:admin')}
 * />
 * ```
 */
export function AnnotationPanel({
  entityType,
  entityId,
  annotations,
  loading,
  error,
  onCreateAnnotation,
  onArchiveAnnotation,
  onLoadThread,
  threads,
  canWrite = false,
  canAdmin = false,
  userScopes = [],
  onSearchMentions,
}: AnnotationPanelProps) {
  const [replyingTo, setReplyingTo] = useState<string | undefined>();

  // Filter to top-level annotations (no parent), active only
  const topLevel = annotations.filter(
    (a) => !a.parentId && a.isActive && !a.archived,
  );

  // Archived annotations shown separately
  const archivedCount = annotations.filter((a) => a.archived).length;

  if (loading) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" variant="light">
        <Text size="sm">{error}</Text>
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      {/* Header */}
      <Group justify="space-between">
        <Group gap="xs">
          <IconMessage size={16} />
          <Text fw={600} size="sm">
            Annotations
          </Text>
          <Badge size="xs" variant="light" color="gray">
            {topLevel.length}
          </Badge>
        </Group>
        {archivedCount > 0 && (
          <Text size="xs" c="dimmed">
            {archivedCount} archived
          </Text>
        )}
      </Group>

      <Divider />

      {/* Annotation list */}
      {topLevel.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No annotations yet. {canWrite && 'Be the first to comment.'}
        </Text>
      ) : (
        <Stack gap="xs">
          {topLevel.map((annotation) => (
            <Box key={annotation.id}>
              <AnnotationItem
                annotation={annotation}
                onReply={canWrite ? setReplyingTo : undefined}
                onArchive={canAdmin ? onArchiveAnnotation : undefined}
                onLoadThread={onLoadThread}
                thread={
                  threads?.[annotation.threadRootId ?? annotation.id]
                }
                canAdmin={canAdmin}
              />

              {/* Inline reply composer */}
              {replyingTo === annotation.id && onCreateAnnotation && (
                <Box ml={24} mt="xs">
                  <AnnotationComposer
                    entityType={entityType}
                    entityId={entityId}
                    parentId={annotation.id}
                    onSubmit={(input) => {
                      onCreateAnnotation(input);
                      setReplyingTo(undefined);
                    }}
                    onCancel={() => setReplyingTo(undefined)}
                    userScopes={userScopes}
                    onSearchMentions={onSearchMentions}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {/* New annotation composer */}
      {canWrite && onCreateAnnotation && !replyingTo && (
        <>
          <Divider />
          <AnnotationComposer
            entityType={entityType}
            entityId={entityId}
            onSubmit={onCreateAnnotation}
            userScopes={userScopes}
            onSearchMentions={onSearchMentions}
          />
        </>
      )}
    </Stack>
  );
}

export default AnnotationPanel;
